// What the Mac app's shell tells the page (mac/Sources/PoulpeMacUI/WindowChrome.swift):
// attributes on <html>, set before any script of ours runs and kept current
// from there. Outside the app none is ever set. And what the page asks of
// the shell on its `boardChrome` message handler, for its workspace: the
// tabs, the panes and their histories are the page's (workspace/), and the
// shell keeps and restores them. Ported from today's lib/shell.ts.
import type { WorkspaceRecovery, WorkspaceState } from '../workspace/protocol'

/** True inside the Mac app's window. */
export function inMacShell(): boolean {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-shell') === 'mac'
}

/** True inside the iPhone app's web view, which marks its page `data-shell="ios"` (ios/). */
export function inPhoneShell(): boolean {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-shell') === 'ios'
}

/** A tab of a pane's row (components/PageTabs.tsx). */
export interface ShellTab {
  /** What the workspace calls the tab: what the row names when it asks for something. */
  id: string
  /** Its page's title, without the app's name. */
  title: string
  /** The tab in front in its pane: the page on screen, which draws the row. */
  active: boolean
  /** Its page's address: what the tab carries as text while it is dragged. */
  href: string
}

/**
 * What a row of tabs asks of the workspace. `drag` is said as a tab starts to
 * be dragged, and with null as the drag ends; `drop` when what is being
 * dragged is dropped on this row, before that tab or at the row's end (null).
 * The workspace knows which tab is being dragged: the row that takes the drop
 * need not.
 */
export type TabRequest = { select: string } | { close: string } | { new: true } | { drag: string | null } | { drop: { before: string | null } }

/**
 * What the page says to the shell: the workspace's readiness, state,
 * recovery, checkpoints and context menus (workspace/model.ts), and
 * `menuTab`, the tab a context menu is about to open on, for the shell's own
 * menu. And `passkey`, a passkey ceremony for the system's own sheet
 * (api/shellPasskeys.ts): `options` is the server's `publicKey`, as it sent
 * it, and the shell answers with a `poulpe-passkey` event carrying the same
 * `id`.
 * `claudeApiKey` keeps Claude API's key in this Mac's Keychain, or forgets
 * it (api/shellClaudeApiKey.ts); the shell answers with a
 * `poulpe-claude-api-key` event.
 * `localRead` asks this Mac's runner's local reads while offline
 * (api/shellLocalReads.ts); the shell answers with a `poulpe-local-read`
 * event.
 * `localWrite` prompts or answers a session of this Mac's runner while
 * offline (api/shellLocalReads.ts); the shell answers with a
 * `poulpe-local-write` event.
 * `nativeStatus` is display-only connection/outbox state; it never confirms
 * a session or authorises native work.
 */
export type ShellMessage =
  | { nativeStatus: { document: string; revision: number; phase: 'loading' | 'ready' | 'error' | 'signed_out'; offline: boolean; stream: 'live' | 'reconnecting' | 'none'; waiting: number; refused: number } }
  | { sessionState: { document: string; revision: number; state: 'confirmed' | 'authenticated' | 'invalidated' } }
  | { workspaceReady: true }
  | { emailLinksReady: { document: string; ready: boolean } }
  | { workspaceUnavailable: true }
  | { workspaceRecovery: WorkspaceRecovery | null }
  | { workspaceCheckpoint: { id: string; recovery: WorkspaceRecovery } }
  | { workspaceState: WorkspaceState }
  | { workspaceCloseWindow: true }
  | { workspaceContext: { tab: string } }
  | { menuTab: string }
  | { passkey: { id: string; document: string; kind: 'create' | 'get'; options: PublicKeyCredentialCreationOptionsJSON | PublicKeyCredentialRequestOptionsJSON } }
  | { claudeApiKey: { id: string; document: string; key: string } | { id: string; document: string; remove: true } }
  | { localRead: { id: string; document: string; read: 'sessions' | 'checks' } | { id: string; document: string; read: 'transcript'; session: string; after?: string } }
  | { localWrite: { id: string; document: string; write: 'prompt'; session: string; prompt: string } | { id: string; document: string; write: 'answer'; session: string; question: string; version: number; replies: unknown[] } }

interface ShellWindow {
  webkit?: { messageHandlers?: { boardChrome?: { postMessage(message: unknown): void } } }
}

/** Whether there is a shell to tell: the Mac app's window. */
export function hasShell(): boolean {
  return typeof window !== 'undefined' && !!(window as unknown as ShellWindow).webkit?.messageHandlers?.boardChrome
}

/** Says it to the shell; outside the app there is nobody to tell. */
export function tellShell(message: ShellMessage): void {
  ;(window as unknown as ShellWindow).webkit?.messageHandlers?.boardChrome?.postMessage(message)
}
